import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  documentCategorySchema,
  householdMemberDocumentSchema,
  workRequirementHouseholdMemberSchema,
  workRequirementSchema,
  workRequirementScreeningAnswersSchema,
  workRequirementScreeningStepEnumSchema,
  workRequirementUsStateRuleSchema,
} from "@libs/db-schemas";

import { HTTPStatusCode } from "../../types";
import { errorSchema } from "../../utils/errors";

export const workRequirementProofKindEnumSchema = z.enum([
  "PROOF_OF_WORK",
  "PROOF_OF_EXEMPTION",
]);

export const workRequirementProgressEnumSchema = z.enum([
  "ACTION_NEEDED",
  "UP_TO_DATE",
  "COMPLETED",
]);

export const workRequirementSlotDocumentSchema =
  householdMemberDocumentSchema.extend({
    category: documentCategorySchema,
    coverageMonth: z.iso.date().nullable(),
  });

export const workRequirementSlotSchema = z.object({
  kind: workRequirementProofKindEnumSchema,
  // First day of the covered month for proof of work; the slot opens that day.
  coverageMonth: z.iso.date().nullable(),
  isLocked: z.boolean(),
  documents: z.array(workRequirementSlotDocumentSchema),
});

export const workRequirementMemberDetailSchema =
  workRequirementHouseholdMemberSchema.extend({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    totalHours: z.number(),
    requiredDocumentCount: z.number(),
    completedDocumentCount: z.number(),
    slots: z.array(workRequirementSlotSchema),
  });

export const workRequirementDetailSchema = workRequirementSchema.extend({
  rule: workRequirementUsStateRuleSchema,
  members: z.array(workRequirementMemberDetailSchema),
  progress: workRequirementProgressEnumSchema,
});

export const updateWorkRequirementScreeningBodySchema = z.object({
  lastStep: workRequirementScreeningStepEnumSchema,
  isComplete: z.boolean(),
  members: z
    .array(workRequirementScreeningAnswersSchema)
    .refine(
      members =>
        new Set(members.map(({ householdMemberId }) => householdMemberId))
          .size === members.length,
      { message: "Household members must be unique" },
    ),
});

const workRequirementMemberPathParamsSchema = z.object({
  workRequirementId: z.uuid(),
  householdMemberId: z.uuid(),
});

const c = initContract();

export const workRequirementsContract = c.router({
  listWorkRequirements: {
    summary: "Get a list of the current patient's work requirements",
    method: "GET",
    path: "/work-requirements",
    responses: {
      [HTTPStatusCode.OK]: z.array(workRequirementDetailSchema),
    },
  },
  getWorkRequirement: {
    summary: "Get a specific work requirement by ID for the current patient",
    method: "GET",
    path: "/work-requirements/:workRequirementId",
    pathParams: z.object({
      workRequirementId: z.uuid(),
    }),
    responses: {
      [HTTPStatusCode.OK]: workRequirementDetailSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
  updateWorkRequirementScreening: {
    summary:
      "Save the screening answers for the household members being checked",
    method: "PUT",
    path: "/work-requirements/:workRequirementId/screening",
    pathParams: z.object({
      workRequirementId: z.uuid(),
    }),
    body: updateWorkRequirementScreeningBodySchema,
    responses: {
      [HTTPStatusCode.OK]: workRequirementDetailSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
  addWorkRequirementDocument: {
    summary:
      "Attach an uploaded document as a household member's work requirement proof",
    method: "POST",
    path: "/work-requirements/:workRequirementId/household-members/:householdMemberId/documents",
    pathParams: workRequirementMemberPathParamsSchema,
    body: z.object({
      householdMemberDocumentId: z.uuid(),
      coverageMonth: z.iso.date().nullish(),
    }),
    responses: {
      [HTTPStatusCode.CREATED]: workRequirementDetailSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
  trackVolunteerOfframpClick: {
    summary:
      "Record that a household member opened the volunteer opportunities link",
    method: "POST",
    path: "/work-requirements/:workRequirementId/household-members/:householdMemberId/volunteer-offramp-clicks",
    pathParams: workRequirementMemberPathParamsSchema,
    body: c.noBody(),
    responses: {
      [HTTPStatusCode.OK]: workRequirementDetailSchema,
      [HTTPStatusCode.BAD_REQUEST]: errorSchema,
      [HTTPStatusCode.NOT_FOUND]: errorSchema,
      [HTTPStatusCode.FORBIDDEN]: errorSchema,
    },
  },
});

export type WorkRequirementSlotType = z.infer<typeof workRequirementSlotSchema>;
export type WorkRequirementMemberDetailType = z.infer<
  typeof workRequirementMemberDetailSchema
>;
export type WorkRequirementDetailType = z.infer<
  typeof workRequirementDetailSchema
>;
export type UpdateWorkRequirementScreeningBodyType = z.infer<
  typeof updateWorkRequirementScreeningBodySchema
>;
